import { createElement } from 'react';
import { z } from 'zod';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    dynamicWidgetEventSourceSchema,
    type DynamicWidgetIssue,
    DynamicWidgetModelBuilder,
    dynamicWidgetResourceSourceSchema,
    getDynamicWidgetSourceId,
    type OutreachProgressWidgetModel,
    outreachProgressWidgetModelSchema,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { OutreachProgressWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/outreach_progress_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

// The two reads the card polls. The contact-attempt route lives under
// /callouts/ for historical reasons but resolves an outreach row, so the
// outreach ID is the path parameter for both.
export const JOINED_OUTREACH_API_PATH =
    '/organizations/{organization_id}/outreaches/joined/{outreach_id}';
export const CONTACT_ATTEMPTS_API_PATH =
    '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts';

export const outreachProgressValidationFields = {
    critical: ['outreach_id'],
    optional: [
        'outreach_type',
        'shift_ids',
        'scheduled_start_at',
        'recurrence_schedule_description',
    ],
} as const;

// A live run returns the outreach ID; a dry run returns `dry_run: true` and
// `would_create_outreach` with no ID at all. Without an ID there is no
// resource to poll, so the entry stays on the classic tool card.
const committedOutputSchema = z
    .object({
        outreach_id: z.string().min(1),
    })
    .passthrough();

const nullableStringSchema = z.string().nullable();
const stringArraySchema = z.array(z.string()).readonly();
const OUTREACH_CREATION_TOOL_NAMES = new Set(['draft_agent_outreach']);

function addOptionalField<T>({
    builder,
    issues,
    sourceId,
    id,
    path,
    rawValue,
    schema,
    fallback,
    renderedValue,
}: {
    readonly builder: DynamicWidgetModelBuilder;
    readonly issues: Array<DynamicWidgetIssue>;
    readonly sourceId: string;
    readonly id: string;
    readonly path: string;
    readonly rawValue: unknown;
    readonly schema: z.ZodType<T>;
    readonly fallback: T;
    readonly renderedValue: (value: T) => string | null;
}): T {
    const parsed =
        rawValue === undefined || rawValue === null
            ? null
            : schema.safeParse(rawValue);
    const value = parsed === null || !parsed.success ? fallback : parsed.data;
    builder.addSourceField({
        id,
        sourceId,
        path,
        rawValue,
        value,
        renderedValue: renderedValue(value),
        presentation: buildBindingPresentation({
            section: 'creation',
            label: id,
        }),
    });
    if (parsed !== null && !parsed.success) {
        issues.push({
            id: `${id}.issue`,
            code: 'source_invalid',
            level: 'warning',
            scope: 'field',
            bindingIds: [id],
            sourceIds: [sourceId],
            retryable: false,
            safeDetails: {
                field: path,
            },
        });
    }
    return value;
}

export function buildOutreachProgressWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): OutreachProgressWidgetModel | null {
    if (
        !OUTREACH_CREATION_TOOL_NAMES.has(entry.name) ||
        entry.callId === null ||
        !entry.toolCallEventId
    ) {
        return null;
    }

    const parsedOutput = committedOutputSchema.safeParse(entry.output);
    if (!parsedOutput.success) {
        return null;
    }
    const output = parsedOutput.data;

    const eventSource = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const outreachResourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'outreach',
        resourceId: output['outreach_id'],
        apiPath: JOINED_OUTREACH_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const attemptsResourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'contact_attempts',
        resourceId: output['outreach_id'],
        apiPath: CONTACT_ATTEMPTS_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const eventSourceId = getDynamicWidgetSourceId(eventSource);
    const outreachSourceId = getDynamicWidgetSourceId(outreachResourceSource);
    const attemptsSourceId = getDynamicWidgetSourceId(attemptsResourceSource);
    const builder = new DynamicWidgetModelBuilder([
        eventSource,
        outreachResourceSource,
        attemptsResourceSource,
    ]);
    const issues: Array<DynamicWidgetIssue> = [];

    const outreachId = builder.addSourceField({
        id: 'outreachId',
        sourceId: eventSourceId,
        path: '/outreach_id',
        rawValue: output['outreach_id'],
        value: output['outreach_id'],
        renderedValue: output['outreach_id'],
        presentation: buildBindingPresentation({
            section: 'creation',
            label: 'outreachId',
        }),
    });
    const outreachType = addOptionalField({
        builder,
        issues,
        sourceId: eventSourceId,
        id: 'outreachType',
        path: '/outreach_type',
        rawValue: output['outreach_type'],
        schema: nullableStringSchema,
        fallback: null,
        renderedValue: (value) => value,
    });
    const shiftIds = addOptionalField({
        builder,
        issues,
        sourceId: eventSourceId,
        id: 'shiftIds',
        path: '/shift_ids',
        rawValue: output['shift_ids'],
        schema: stringArraySchema,
        fallback: [],
        renderedValue: (value) =>
            value.length === 0 ? null : JSON.stringify(value),
    });
    const scheduledStartAt = addOptionalField({
        builder,
        issues,
        sourceId: eventSourceId,
        id: 'scheduledStartAt',
        path: '/scheduled_start_at',
        rawValue: output['scheduled_start_at'],
        schema: nullableStringSchema,
        fallback: null,
        renderedValue: (value) => value,
    });
    const recurrenceDescription = addOptionalField({
        builder,
        issues,
        sourceId: eventSourceId,
        id: 'recurrenceDescription',
        path: '/recurrence_schedule_description',
        rawValue: output['recurrence_schedule_description'],
        schema: nullableStringSchema,
        fallback: null,
        renderedValue: (value) => value,
    });

    // Everything the card shows about what is happening right now comes from
    // a poll, not from the transcript. Declaring the live fields keeps the
    // "every dynamic value names its source" rule true for them too.
    builder.addLiveField({
        id: 'live.status',
        sourceIds: [outreachSourceId],
        paths: ['/display_status', '/status'],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'status',
        }),
    });
    builder.addLiveField({
        id: 'live.responseCounts',
        sourceIds: [outreachSourceId],
        paths: ['/response_counts'],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'responseCounts',
        }),
    });
    builder.addLiveField({
        id: 'live.clients',
        sourceIds: [outreachSourceId],
        paths: ['/shifts/*/patient_name', '/shifts/*/client_id'],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'clients',
        }),
    });
    builder.addLiveField({
        id: 'live.queuedCaregivers',
        sourceIds: [outreachSourceId],
        paths: ['/suggested_caregivers'],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'queuedCaregivers',
        }),
    });
    builder.addLiveField({
        id: 'live.progress',
        sourceIds: [outreachSourceId, attemptsSourceId],
        paths: [
            '/suggested_caregivers',
            '/*/caregiver_id',
            '/*/outreach_status',
        ],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'progress',
        }),
    });
    builder.addLiveField({
        id: 'live.attemptRows',
        sourceIds: [attemptsSourceId],
        paths: [
            '/*/caregiver_id',
            '/*/org_caregiver_record/name',
            '/*/delivery_mode',
            '/*/outreach_status',
            '/*/distance',
            '/*/response_timestamp',
            '/*/shift_responses',
            '/*/transcript_text',
        ],
        presentation: buildBindingPresentation({
            section: 'live',
            label: 'attemptRows',
        }),
    });

    return outreachProgressWidgetModelSchema.parse({
        ...builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'progress'),
            templateVersion: 1,
            issues,
        }),
        template: 'progress',
        templateVersion: 1,
        outreachId,
        outreachType,
        shiftIds,
        scheduledStartAt,
        recurrenceDescription,
    });
}

export const outreachProgressAdapter: DynamicWidgetAdapter<OutreachProgressWidgetModel> =
    {
        template: 'progress',
        templateVersion: 1,
        toolNames: OUTREACH_CREATION_TOOL_NAMES,
        build: buildOutreachProgressWidgetModel,
        render(model, handlers) {
            return createElement(OutreachProgressWidget, {
                model,
                onCaregiverClick: handlers.onCaregiverClick,
            });
        },
    };
